import { useMemo, useState } from 'react';
import { Box, Chip, Divider, Paper, Tab, Table, TableBody, TableCell, TableRow, Tabs, Typography } from '@mui/material';
import { AnalysisScope, type AnalysisIssue, type MessageAnalysisResult, type MessageEvent } from '@k-ucap/shared';
import { createMessageDictionaryService } from '@/domain/message/MessageDictionaryService.js';
import { getSeverityColor } from './utils.js';

export interface AnalysisDetailPanelProps {
  event: MessageEvent | null;
  result: MessageAnalysisResult | null;
  selectedIssue: AnalysisIssue | null;
  events: MessageEvent[];
  onSelectEvent?: (event: MessageEvent) => void;
}

const scopeTitle: Record<AnalysisScope, string> = {
  [AnalysisScope.PACKET]: 'Packet',
  [AnalysisScope.HEADER]: 'Header',
  [AnalysisScope.FIELD]: 'Field',
  [AnalysisScope.ROLE]: 'Role',
  [AnalysisScope.SEQUENCE]: 'Sequence',
  [AnalysisScope.TIMING]: 'Timing',
  [AnalysisScope.CHECKSUM]: 'Checksum',
  [AnalysisScope.DEFINITION]: 'Definition',
  [AnalysisScope.ENCODING]: 'Encoding',
};

const AnalysisTab = ({
  event,
  result,
  selectedIssue,
}: {
  event: MessageEvent;
  result: MessageAnalysisResult;
  selectedIssue: AnalysisIssue | null;
}) => {
  const issue = selectedIssue;

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          Message
        </Typography>
        <Typography variant="body1">
          #{event.messageType} {event.name}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {event.sourceRole || event.source} → {event.targetRole || event.target}
        </Typography>
      </Box>

      <Divider sx={{ my: 2 }} />

      {issue ? (
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Chip label={issue.severity} size="small" color={getSeverityColor(issue.severity)} />
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
              {issue.code}
            </Typography>
          </Box>
          <Typography variant="body2" gutterBottom>
            {issue.message}
          </Typography>
          <Table size="small" sx={{ mt: 1 }}>
            <TableBody>
              <TableRow>
                <TableCell component="th" scope="row" sx={{ width: 120, fontWeight: 'bold' }}>
                  Scope
                </TableCell>
                <TableCell>{scopeTitle[(issue.scope as AnalysisScope) ?? AnalysisScope.PACKET]}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                  Path
                </TableCell>
                <TableCell>{issue.path || '-'}</TableCell>
              </TableRow>
              {issue.expected !== undefined && (
                <TableRow>
                  <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                    Expected
                  </TableCell>
                  <TableCell>{issue.expected}</TableCell>
                </TableRow>
              )}
              {issue.actual !== undefined && (
                <TableRow>
                  <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                    Actual
                  </TableCell>
                  <TableCell>{issue.actual}</TableCell>
                </TableRow>
              )}
              {issue.ruleSource && (
                <TableRow>
                  <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                    Rule Source
                  </TableCell>
                  <TableCell>{issue.ruleSource}</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
      ) : (
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            Overall Summary
          </Typography>
          <Table size="small">
            <TableBody>
              <TableRow>
                <TableCell component="th" scope="row" sx={{ width: 120, fontWeight: 'bold' }}>
                  Overall
                </TableCell>
                <TableCell>
                  <Chip label={result.overallStatus} size="small" color={getSeverityColor(result.overallStatus)} />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                  Definition
                </TableCell>
                <TableCell>
                  <Chip
                    label={result.definitionStatus}
                    size="small"
                    color={getSeverityColor(result.definitionStatus)}
                  />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                  Dictionary
                </TableCell>
                <TableCell>{result.dictionaryVersion ?? '-'}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                  Analyzer
                </TableCell>
                <TableCell>{result.analyzerVersion}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Box>
      )}

      <Divider sx={{ my: 2 }} />

      <Typography variant="subtitle2" gutterBottom>
        Summary Counts
      </Typography>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <Chip label={`PASS ${result.summary.pass}`} size="small" color="success" />
        <Chip label={`WARN ${result.summary.warn}`} size="small" color="warning" />
        <Chip label={`FAIL ${result.summary.fail}`} size="small" color="error" />
        <Chip label={`UNKNOWN ${result.summary.unknown}`} size="small" color="info" />
        <Chip label={`UNSUPPORTED ${result.summary.unsupported}`} size="small" color="default" />
      </Box>
    </Box>
  );
};

const PayloadJsonTab = ({ event }: { event: MessageEvent }) => {
  const json = useMemo(() => JSON.stringify(event.payload ?? event.fields ?? {}, null, 2), [event]);
  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        Payload JSON
      </Typography>
      <Box
        component="pre"
        sx={{
          p: 1,
          bgcolor: 'background.default',
          borderRadius: 1,
          overflow: 'auto',
          maxHeight: 400,
          fontSize: '0.85rem',
        }}
      >
        {json}
      </Box>
    </Box>
  );
};

const RawHexTab = ({ event }: { event: MessageEvent }) => {
  const hex = event.rawHex ?? '';
  const bytes = event.rawBytes ?? [];
  const ascii = bytes.map((b) => (b >= 32 && b <= 126 ? String.fromCharCode(b) : '.')).join('');
  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        Raw HEX (PoC Preview Encoding)
      </Typography>
      <Box
        sx={{
          p: 1,
          bgcolor: 'background.default',
          borderRadius: 1,
          overflow: 'auto',
          maxHeight: 400,
          fontFamily: 'monospace',
          fontSize: '0.85rem',
        }}
      >
        {hex ? (
          <Box>
            <Box sx={{ wordBreak: 'break-all', mb: 1 }}>{hex}</Box>
            <Divider sx={{ my: 1 }} />
            <Box>ASCII: {ascii}</Box>
            <Box>Bytes: {bytes.length}</Box>
          </Box>
        ) : (
          <Typography color="text.secondary">rawHex 없음</Typography>
        )}
      </Box>
    </Box>
  );
};

const DefinitionTab = ({ event }: { event: MessageEvent }) => {
  const dictionaryService = useMemo(() => createMessageDictionaryService(), []);
  const definition = useMemo(() => dictionaryService.getMessageByType(event.messageType), [dictionaryService, event]);

  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        Message Definition
      </Typography>
      {definition ? (
        <Box
          component="pre"
          sx={{
            p: 1,
            bgcolor: 'background.default',
            borderRadius: 1,
            overflow: 'auto',
            maxHeight: 400,
            fontSize: '0.85rem',
          }}
        >
          {JSON.stringify(definition, null, 2)}
        </Box>
      ) : (
        <Typography color="text.secondary">정의를 찾을 수 없습니다.</Typography>
      )}
    </Box>
  );
};

const HistoryTab = ({
  events,
  selectedEvent,
  onSelectEvent,
}: {
  events: MessageEvent[];
  selectedEvent: MessageEvent | null;
  onSelectEvent?: (event: MessageEvent) => void;
}) => {
  const history = useMemo(() => {
    if (!selectedEvent) return [];
    return events.filter((e) => e.messageType === selectedEvent.messageType && e.id !== selectedEvent.id).slice(0, 20);
  }, [events, selectedEvent]);

  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        History (same message type)
      </Typography>
      {history.length === 0 ? (
        <Typography color="text.secondary">이력이 없습니다.</Typography>
      ) : (
        <Box>
          {history.map((e) => (
            <Box
              key={e.id}
              onClick={() => onSelectEvent?.(e)}
              sx={{
                p: 1,
                mb: 1,
                borderRadius: 1,
                border: 1,
                borderColor: 'divider',
                cursor: onSelectEvent ? 'pointer' : 'default',
                '&:hover': onSelectEvent ? { bgcolor: 'action.hover' } : {},
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="body2">#{e.messageType}</Typography>
                <Typography variant="body2">{new Date(e.timestamp).toLocaleTimeString()}</Typography>
                <Chip label={e.validation || 'N/A'} size="small" color={getSeverityColor(e.validation || '')} />
              </Box>
              <Typography variant="caption" color="text.secondary">
                Seq {e.sequenceNumber ?? '-'} | {e.sourceRole || e.source} → {e.targetRole || e.target}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export const AnalysisDetailPanel = ({
  event,
  result,
  selectedIssue,
  events,
  onSelectEvent,
}: AnalysisDetailPanelProps) => {
  const [tab, setTab] = useState(0);

  if (!event || !result) {
    return (
      <Paper sx={{ height: '100%', p: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography color="text.secondary">상세 분석 항목을 선택하세요.</Typography>
      </Paper>
    );
  }

  return (
    <Paper sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto">
        <Tab label="Analysis" />
        <Tab label="Payload JSON" />
        <Tab label="Raw HEX" />
        <Tab label="Definition" />
        <Tab label="History" />
      </Tabs>
      <Box sx={{ p: 2, overflow: 'auto', flexGrow: 1 }}>
        {tab === 0 && <AnalysisTab event={event} result={result} selectedIssue={selectedIssue} />}
        {tab === 1 && <PayloadJsonTab event={event} />}
        {tab === 2 && <RawHexTab event={event} />}
        {tab === 3 && <DefinitionTab event={event} />}
        {tab === 4 && <HistoryTab events={events} selectedEvent={event} onSelectEvent={onSelectEvent} />}
      </Box>
    </Paper>
  );
};
